import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Bell, 
  X, 
  FileSpreadsheet, 
  RefreshCw,
  ChevronRight,
  Check,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '../config/api';

const NotificationBell = ({ userId, onNewFilesClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadUnreadCount();
    
    // Check for new files every 5 minutes
    const interval = setInterval(() => {
      checkForNewFiles();
    }, 5 * 60 * 1000);
    
    // Initial check
    checkForNewFiles();
    
    return () => clearInterval(interval);
  }, [userId]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/sync/notifications/count?user_id=${userId || ''}`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sync/notifications?user_id=${userId || ''}`);
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewFiles = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync/scan`);
      if (response.data.new_files_count > 0) {
        loadUnreadCount();
        loadNotifications();
      }
    } catch (error) {
      console.error('Error scanning for new files:', error);
    } finally {
      setSyncing(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/sync/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/sync/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      loadUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      loadNotifications();
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    if (notification.type === 'new_files' && onNewFilesClick) {
      onNewFilesClick(notification.data);
      setIsOpen(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    return date.toLocaleDateString('es-AR');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBellClick}
        className="relative text-gray-400 hover:text-white"
      >
        {syncing ? (
          <RefreshCw className="w-5 h-5 animate-spin" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full text-xs text-white flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-neutral-900 border border-red-900/50 rounded-lg shadow-xl z-50">
          <div className="p-3 border-b border-red-900/30 flex items-center justify-between">
            <h3 className="font-semibold text-white">Notificaciones</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={checkForNewFiles}
              disabled={syncing}
              className="text-gray-400 hover:text-white h-8 px-2"
              title="Buscar nuevos archivos"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 mx-auto text-red-500 animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay notificaciones</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-neutral-800 hover:bg-neutral-800/50 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-red-950/20' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notification.type === 'new_files' ? 'bg-green-600/20' : 'bg-blue-600/20'
                    }`}>
                      <FileSpreadsheet className={`w-4 h-4 ${
                        notification.type === 'new_files' ? 'text-green-500' : 'text-blue-500'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-white truncate">
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="text-gray-500 hover:text-red-500 p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{notification.message}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {formatTime(notification.created_at)}
                        </span>
                        {!notification.read && (
                          <Badge className="bg-red-600 text-white text-xs px-1.5 py-0">
                            Nuevo
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {notification.type === 'new_files' && notification.data?.new_files && (
                    <div className="mt-2 ml-11">
                      <div className="text-xs text-gray-400 space-y-1">
                        {notification.data.new_files.slice(0, 3).map((file, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            <FileSpreadsheet className="w-3 h-3 text-green-500" />
                            <span className="truncate">{file.file_name}</span>
                          </div>
                        ))}
                        {notification.data.new_files.length > 3 && (
                          <p className="text-gray-500">
                            +{notification.data.new_files.length - 3} más...
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="mt-2 h-7 bg-green-600 hover:bg-green-700 text-white text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNotificationClick(notification);
                        }}
                      >
                        Ver archivos <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
